import React, { useState, useEffect, useCallback } from "react";
import { Drawer, DrawerProps } from "antd";
import { useIsMobile, useIsTablet, useKeyboardShortcuts } from "../../Hook";
import { SHORTCUTS } from "../../Constant";

interface ResizableDrawerProps extends DrawerProps {
  initialWidthPercent?: number;
  minWidth?: number;
  bodyPadding?: string;
}

export const ResizableDrawer: React.FC<ResizableDrawerProps> = ({
  initialWidthPercent = 0.5,
  minWidth = 320,
  bodyPadding = "5px",
  placement = "right",
  children,
  width,
  open,
  onClose,
  ...props
}) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const isOpen = Boolean(open ?? (props as any).visible);

  useKeyboardShortcuts(
    [
      {
        shortcut: SHORTCUTS.CHECKOUT.CLOSE_DRAWER,
        options: {
          enabled: Boolean(isOpen && onClose),
          enableOnFormTags: true,
          enableInModalOrDrawer: true,
        },
        handler: (e) => {
          e.preventDefault();
          e.stopPropagation();
          onClose?.(e as any);
        },
      },
    ],
    {
      enabled: Boolean(isOpen && onClose),
      enableInModalOrDrawer: true,
    },
  );

  const [drawerWidth, setDrawerWidth] = useState<number>(() => {
    if (typeof width === "number") {
      return width;
    }
    if (typeof width === "string") {
      if (width.endsWith("%")) {
        const percent = parseFloat(width) / 100;
        if (!isNaN(percent)) {
          return window.innerWidth * percent;
        }
      }
      const num = parseFloat(width);
      if (!isNaN(num)) {
        return num;
      }
    }
    if (isMobile || isTablet) return window.innerWidth;
    return Math.max(400, window.innerWidth * initialWidthPercent);
  });

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isMobile || isTablet) return;
      e.preventDefault();

      const handleElement = e.currentTarget as HTMLElement;
      const wrapper = handleElement.closest(
        ".ant-drawer-content-wrapper",
      ) as HTMLElement;
      if (!wrapper) return;

      const startX = e.clientX;
      const startWidth = wrapper.getBoundingClientRect().width;

      document.body.style.cursor = "ew-resize";
      document.body.style.userSelect = "none";

      let currentWidth = startWidth;
      let animationFrameId: number | null = null;

      const onMouseMove = (moveEvent: MouseEvent) => {
        if (animationFrameId !== null) {
          cancelAnimationFrame(animationFrameId);
        }
        animationFrameId = requestAnimationFrame(() => {
          const deltaX = moveEvent.clientX - startX;
          const direction = placement === "left" ? 1 : -1;

          currentWidth = Math.max(
            minWidth,
            Math.min(window.innerWidth - 100, startWidth + deltaX * direction),
          );
          wrapper.style.width = `${currentWidth}px`;
        });
      };

      const onMouseUp = () => {
        if (animationFrameId !== null) {
          cancelAnimationFrame(animationFrameId);
        }
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);

        setDrawerWidth(currentWidth);
      };

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [isMobile, isTablet, placement, minWidth],
  );

  useEffect(() => {
    if (isMobile || isTablet) {
      setDrawerWidth(window.innerWidth);
      return;
    }
    let resizeFrameId: number | null = null;
    const handleResize = () => {
      if (resizeFrameId !== null) {
        cancelAnimationFrame(resizeFrameId);
      }
      resizeFrameId = requestAnimationFrame(() => {
        setDrawerWidth((prev) => Math.min(prev, window.innerWidth - 100));
      });
    };
    window.addEventListener("resize", handleResize);
    return () => {
      if (resizeFrameId !== null) {
        cancelAnimationFrame(resizeFrameId);
      }
      window.removeEventListener("resize", handleResize);
    };
  }, [isMobile, isTablet]);

  const isLeft = placement === "left";
  const isRight = placement === "right";
  const isHorizontal = isLeft || isRight;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement={placement}
      width={isMobile || isTablet ? "100%" : isHorizontal ? drawerWidth : width}
      bodyStyle={{ padding: 0, position: "relative", overflow: "hidden" }}
      {...props}
    >
      <div
        style={{
          height: "100%",
          overflowY: "auto",
          overflowX: "hidden",
          padding: bodyPadding,
          marginLeft: isHorizontal && !isLeft ? "6px" : 0,
          marginRight: isHorizontal && isLeft ? "6px" : 0,
          border: "none",
          outline: "none",
        }}
      >
        {children}
      </div>

      {isHorizontal && (
        <div
          onMouseDown={handleMouseDown}
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            width: "6px",
            [isLeft ? "right" : "left"]: 0,
            cursor: isMobile || isTablet ? "default" : "ew-resize",
            background: "transparent",
            zIndex: 10,
            transition: "background 0.2s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onMouseEnter={(e) => {
            if (!isMobile && !isTablet)
              e.currentTarget.style.background = "rgba(0,0,0,0.05)";
          }}
          onMouseLeave={(e) => {
            if (!isMobile && !isTablet)
              e.currentTarget.style.background = "transparent";
          }}
        >
          <div
            style={{
              width: "2px",
              height: "40px",
              background:
                "linear-gradient(180deg, transparent, currentColor, transparent)",
              opacity: 0.3,
              color: "inherit",
            }}
          />
        </div>
      )}
    </Drawer>
  );
};
